import { useState, useEffect, useCallback } from 'react';

import { paths } from 'src/routes/paths';
import { useRouter, useSearchParams } from 'src/routes/hooks';

import { SplashScreen } from 'src/components/loading-screen';

import useAuthStore, { waitForAuthReady } from '../store/firebase/auth-store';

// ----------------------------------------------------------------------

type Props = {
  children: React.ReactNode;
};

export default function GuestGuard({ children }: Props) {
  const [isReady, setIsReady] = useState(false);

  console.error('[GuestGuard] RENDER:', { isReady });

  // Wait for Firebase auth to be ready - in useEffect, NOT during render!
  useEffect(() => {
    let mounted = true;
    console.error('[GuestGuard] useEffect - Waiting for auth ready...');

    waitForAuthReady().then(() => {
      console.error('[GuestGuard] waitForAuthReady resolved, mounted:', mounted);
      if (mounted) {
        setIsReady(true);
      }
    });

    return () => {
      console.error('[GuestGuard] Cleanup - unmounting');
      mounted = false;
    };
  }, []);

  if (!isReady) {
    console.error('[GuestGuard] Showing SplashScreen (waiting for auth ready)');
    return <SplashScreen />;
  }

  return <Container>{children}</Container>;
}

// ----------------------------------------------------------------------

function Container({ children }: Props) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const returnTo = searchParams.get('returnTo') || paths.dashboard.root;
  const { authenticated, authInitialized, user } = useAuthStore();

  console.error('[GuestGuard.Container] RENDER:', {
    authenticated,
    authInitialized,
    hasUser: !!user,
    returnTo,
  });

  const check = useCallback(() => {
    console.error('[GuestGuard.Container] check():', { authInitialized, authenticated, returnTo });
    if (authInitialized && authenticated) {
      console.error('[GuestGuard.Container] ✅ REDIRECTING TO:', returnTo);
      router.replace(returnTo);
    } else {
      console.error('[GuestGuard.Container] Not redirecting - staying on login page');
    }
  }, [authenticated, authInitialized, returnTo, router]);

  useEffect(() => {
    check();
  }, [check]);

  return <>{children}</>;
}
